// src/components/CategoryPage/CategoryPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from '../../utils/axiosConfig';
import useLocalizedPath from '../../hooks/useLocalizedPath';
import './CategoryPage.css';

const CategoryPage = () => {
	const { t } = useTranslation();
	const { currentLanguage } = useLanguage();
	const [products, setProducts] = useState([]);
	const location = useLocation();
	const navigate = useNavigate();
	const localizedPath = useLocalizedPath();

	const useQuery = () => new URLSearchParams(location.search);
	const category = useQuery().get('category');

	useEffect(() => {
		const fetchProducts = async () => {
			try {
				if (currentLanguage === 'en') {
					// For English, use the same logic as ProductOrdering
					const promises = [
						axios.get(`/api/products/category/${encodeURIComponent(category)}`, { 
							headers: { 
								'Cache-Control': 'no-cache',
								'Pragma': 'no-cache'
							},
							params: { t: Date.now() } // Add timestamp to bust cache
						}),
						axios.get('/api/english-products', { 
							headers: { 
								'Cache-Control': 'no-cache',
								'Pragma': 'no-cache'
							},
							params: { t: Date.now() } // Add timestamp to bust cache
						}).catch(err => {
							// If English products endpoint fails (404), return empty array
							if (err.response?.status === 404) {
								return { data: [] };
							}
							throw err;
						})
					];
					
					const [turkishResponse, englishResponse] = await Promise.all(promises);
					
					const turkishProducts = Array.isArray(turkishResponse.data) ? turkishResponse.data : [];
					const allEnglishProducts = Array.isArray(englishResponse.data) ? englishResponse.data : [];
					
					// Filter English products by category
					const englishProducts = allEnglishProducts.filter(product => 
						product.category.toLowerCase() === category.toLowerCase()
					);
					
					// Filter Turkish products to only include those with translations (like ProductOrdering does)
					const translatedTurkishProducts = turkishProducts.filter(product => {
						return product.hasEnglishTranslation === true;
					});
					
					// Combine products and sort by order (exactly like ProductOrdering)
					const combinedProducts = [...translatedTurkishProducts, ...englishProducts];
					combinedProducts.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
					
					console.log('CategoryPage - English view products:', {
						turkishProducts: turkishProducts.map(p => ({ title: p.title, order: p.order, hasTranslation: p.hasEnglishTranslation })),
						translatedTurkishProducts: translatedTurkishProducts.map(p => ({ title: p.title, order: p.order })),
						englishProducts: englishProducts.map(p => ({ title: p.title, order: p.order })),
						combinedProducts: combinedProducts.map(p => ({ title: p.title, order: p.order }))
					});
					
					setProducts(combinedProducts);
				} else {
					// For Turkish, fetch products by category (like ProductOrdering does)
					const response = await axios.get(`/api/products/category/${encodeURIComponent(category)}`, {
						headers: { 
							'Cache-Control': 'no-cache',
							'Pragma': 'no-cache'
						},
						params: { t: Date.now() } // Add timestamp to bust cache
					});
					const turkishProducts = response.data || [];
					turkishProducts.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
					
					console.log('CategoryPage - Turkish view products:', {
						turkishProducts: turkishProducts.map(p => ({ title: p.title, order: p.order }))
					});
					
					setProducts(turkishProducts);
				}
			} catch (error) {
				console.error('Error fetching products:', error);
			}
		};
		fetchProducts();
	}, [currentLanguage, category]); // Also refresh when category changes

	const filteredProducts = Array.isArray(products) ? products.filter(
		(product) => product.category.toLowerCase() === category.toLowerCase()
	).sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)) : [];

	const handleCardClick = (productId) => {
		navigate(localizedPath(`/products/${productId}`));
		window.scrollTo(0, 0); // Scroll to the top of the page
	};

	// Normalize a title to a stable i18n key like "grill_cold"
	const normalizeToKey = (text) =>
		(text || '')
			.toLowerCase()
			.replace(/ç/g, 'c')
			.replace(/ğ/g, 'g')
			.replace(/ı/g, 'i')
			.replace(/ş/g, 's')
			.replace(/ö/g, 'o')
			.replace(/ü/g, 'u')
			.replace(/[^a-z0-9]+/g, '_')
			.replace(/^_+|_+$/g, '');

	// Resolve a product-specific translation key
	const getProductKey = (product) => product.translationKey || product._id || normalizeToKey(product.title);

	// Helpers to translate title/description with fallback
	// These are now reactive to language changes via useMemo
	const getTranslatedTitle = useMemo(() => {
		return (product) => {
			const key = getProductKey(product);
			const k = `product_items.${key}.title`;
			const val = t(k);
			return val === k ? product.title : val;
		};
	}, [t, currentLanguage]);

	const getTranslatedDescription = useMemo(() => {
		return (product) => {
			const key = getProductKey(product);
			const k = `product_items.${key}.description`;
			const val = t(k);
			return val === k ? product.description : val;
		};
	}, [t, currentLanguage]);

	// Memoize products with computed MSDS URLs to be reactive to language changes
	const productsWithMsds = useMemo(() => {
		return filteredProducts.map(product => ({
			...product,
			msdsUrl: currentLanguage === 'en' ? (product.msdsEn || product.msds) : product.msds
		}));
	}, [filteredProducts, currentLanguage]);

	/**
	 * Helper function to get the 640w image URL from the images array.
	 * It assumes that each image has a corresponding 640w version.
	 * If no 640w image is found, it falls back to the first image.
	 * @param {Array} images - Array of image URLs.
	 * @returns {String} - URL of the 640w image or a fallback image.
	 */
	const get640wImage = (images) => {
		// Since all images are now square, you can directly return the 640w image
		const image640w = images.find(url => url.includes('-640w.'));
		return image640w || images[0];
	};


	return (
		<Container className="unique-category-page mt-4">
			<Row className="g-4">
				{productsWithMsds.length > 0 ? (
					productsWithMsds.map((product) => (
						<Col md={3} key={product._id}>
							<Card
								className="unique-category-card h-100"
								onClick={() => handleCardClick(product._id)}
								style={{ cursor: 'pointer' }}
							>
								{product.images && product.images.length > 0 && (
									// Inside CategoryPage.js

									<div className="unique-category-img-container">
										<img
											src={get640wImage(product.images)}
											alt={getTranslatedTitle(product)}
											className="unique-category-img"
											loading="lazy"
										/>
									</div>

								)}
								<Card.Body className="d-flex flex-column unique-card-body">
									<Card.Title className="unique-card-title">{getTranslatedTitle(product)}</Card.Title>
									<Card.Text className="unique-card-text">{getTranslatedDescription(product)}</Card.Text>
									{product.msdsUrl && (
										<a
											href={product.msdsUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="btn btn-primary unique-btn-download mt-auto"
											onClick={(e) => { e.stopPropagation(); }}
										>
											{t('products.msds_download')}
										</a>
									)}
								</Card.Body>
							</Card>
						</Col>
					))
				) : (
					<p className="unique-no-products">{t('products.no_products')}</p>
				)}
			</Row>
		</Container>
	);
};

export default CategoryPage;
